import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { PageHeader } from "@/components/ui/page-header";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { 
  FileText, 
  Calendar, 
  Trash2, 
  Target,
  TrendingUp,
  Building,
  Eye,
  Download,
  Upload,
  Plus,
  ExternalLink,
  Sparkles,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ArrowLeft
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ResumeLibraryService, SavedResume } from "@/services/resumeLibraryService";
import { useAuth } from "@/hooks/useAuth";
import { PDFViewer } from "@/components/PDFViewer";
import { WordOnlineViewer } from "@/components/WordOnlineViewer";
import { supabase } from "@/integrations/supabase/client";
import microsoftWordLogo from "@/assets/logos/microsoft-word-logo.png";
import pdfLogo from "@/assets/logos/pdf-logo.png";

interface ResumeLibraryEnhancedProps {
  onBack: () => void;
}

export function ResumeLibraryEnhanced({ onBack }: ResumeLibraryEnhancedProps) {
  const [savedResumes, setSavedResumes] = useState<SavedResume[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [selectedResume, setSelectedResume] = useState<SavedResume | null>(null);
  const [selectedResumes, setSelectedResumes] = useState<Set<string>>(new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<{resumeId: string, title: string} | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<string>('date-desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      loadSavedResumes();
    }
  }, [user]);

  const loadSavedResumes = async () => {
    setIsLoading(true);
    try {
      console.log('ResumeLibraryEnhanced: Loading saved resumes...');
      const resumes = await ResumeLibraryService.getSavedResumes();
      console.log('ResumeLibraryEnhanced: Loaded resumes:', resumes.length, resumes.map(r => ({id: r.id, title: r.title})));
      setSavedResumes(resumes);
    } catch (error) {
      console.error('Error loading saved resumes:', error);
      toast({
        title: "Error",
        description: "Failed to load saved resumes. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectResume = (resumeId: string, checked: boolean) => {
    const newSelected = new Set(selectedResumes);
    if (checked) {
      newSelected.add(resumeId);
    } else {
      newSelected.delete(resumeId);
    }
    setSelectedResumes(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedResumes(new Set(savedResumes.map(resume => resume.id)));
    } else {
      setSelectedResumes(new Set());
    }
  };

  const confirmDeleteResume = async () => {
    if (!showDeleteDialog) return;
    
    const { resumeId, title } = showDeleteDialog;
    
    try {
      console.log('ResumeLibraryEnhanced: Starting delete for resume:', resumeId, title);
      setIsDeleting(resumeId);
      await ResumeLibraryService.deleteResume(resumeId);
      console.log('ResumeLibraryEnhanced: Delete successful, reloading list...');
      
      toast({
        title: "Resume Deleted",
        description: "Resume has been removed from your library.",
      });
      
      if (selectedResume?.id === resumeId) {
        setSelectedResume(null);
      }
      
      // Remove from selected resumes if it was selected
      const newSelected = new Set(selectedResumes);
      newSelected.delete(resumeId);
      setSelectedResumes(newSelected);
      
      setShowDeleteDialog(null);
      await loadSavedResumes();
      console.log('ResumeLibraryEnhanced: Reload completed');
      
    } catch (error) {
      console.error('Error deleting resume:', error);
      toast({
        title: "Delete Failed",
        description: "Failed to delete resume. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(null);
    }
  };

  const handleBulkDelete = async () => {
    try {
      setIsBulkDeleting(true);
      const deletePromises = Array.from(selectedResumes).map(resumeId => 
        ResumeLibraryService.deleteResume(resumeId)
      );
      
      await Promise.all(deletePromises);
      
      toast({
        title: "Resumes Deleted",
        description: `${selectedResumes.size} resume(s) have been removed from your library.`
      });
      
      // Clear selections
      setSelectedResumes(new Set());
      setShowBulkDeleteDialog(false);
      
      // Clear selected resume if it was deleted
      if (selectedResume && selectedResumes.has(selectedResume.id)) {
        setSelectedResume(null);
      }
      
      // Refresh the library
      await loadSavedResumes();
      
    } catch (error) {
      console.error('Error deleting resumes:', error);
      toast({
        title: "Bulk Delete Failed",
        description: "Failed to delete some resumes. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleDeleteResume = async (resumeId: string, title: string) => {
    setShowDeleteDialog({ resumeId, title });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleFileUpload = async (file: File) => {
    if (!user) return;

    setIsUploading(true);
    try {
      const title = file.name.replace(/\.[^/.]+$/, ""); // Remove file extension
      
      // For now, we'll save without parsing - parsing can be added later
      await ResumeLibraryService.saveResumeWithFile(title, file);
      
      toast({
        title: "Resume Uploaded",
        description: `${file.name} has been successfully uploaded to your library.`,
      });
      
      // Refresh the library
      await loadSavedResumes();
      
    } catch (error) {
      console.error('Error uploading resume:', error);
      toast({
        title: "Upload Failed",
        description: "Failed to upload resume. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const triggerFileUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.doc,.docx';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        handleFileUpload(file);
      }
    };
    input.click();
  };

  const generateSignedUrl = async (filePath: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('resumes')
        .createSignedUrl(filePath, 3600); // 1 hour expiry

      if (error) {
        console.error('Error generating signed URL:', error);
        toast({
          title: "Error",
          description: "Failed to generate file access URL",
          variant: "destructive",
        });
        return null;
      }

      return data.signedUrl;
    } catch (error) {
      console.error('Error generating signed URL:', error);
      return null;
    }
  };

  const generatePDFBlobUrl = async (filePath: string) => {
    try {
      console.log('ResumeLibrary: Starting PDF download for path:', filePath);
      
      const { data, error } = await supabase.storage
        .from('resumes')
        .download(filePath);

      if (error) {
        console.error('ResumeLibrary: Error downloading PDF:', error);
        toast({
          title: "Error",
          description: "Failed to download PDF file",
          variant: "destructive",
        });
        return null;
      }

      console.log('ResumeLibrary: PDF downloaded successfully, size:', data.size, 'bytes');
      console.log('ResumeLibrary: PDF data type:', data.type);

      // Create blob URL for PDF.js
      const blob = new Blob([data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      
      console.log('ResumeLibrary: Created blob URL:', blobUrl);
      
      return blobUrl;
    } catch (error) {
      console.error('ResumeLibrary: Error downloading PDF:', error);
      return null;
    }
  };

  const handleDownload = async (resume: SavedResume) => {
    if (!resume.originalFilePath) {
      toast({
        title: "No File Available",
        description: "This resume doesn't have an associated file for download.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data, error } = await supabase.storage
        .from('resumes')
        .download(resume.originalFilePath);

      if (error) {
        console.error('Error downloading file:', error);
        toast({
          title: "Download Failed",
          description: "Failed to download the file. Please try again.",
          variant: "destructive",
        });
        return;
      }

      // Create blob and download
      const blob = new Blob([data], { type: resume.fileType || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = resume.fileName || `${resume.title}.${resume.fileType?.includes('pdf') ? 'pdf' : 'docx'}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Download Started",
        description: `${resume.fileName || resume.title} download started.`,
      });

    } catch (error) {
      console.error('Error downloading file:', error);
      toast({
        title: "Download Failed",
        description: "Failed to download the file. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handlePreviewClick = async (resume: SavedResume) => {
    if (!resume.originalFilePath) {
      toast({
        title: "No File Available",
        description: "This resume doesn't have an associated file for preview.",
        variant: "destructive",
      });
      return;
    }

    // For PDFs, use blob download approach to avoid CORS issues
    if (resume.fileType === 'application/pdf') {
      const blobUrl = await generatePDFBlobUrl(resume.originalFilePath);
      if (blobUrl) {
        setSignedUrl(blobUrl);
        setShowPreview(true);
      }
    } else {
      // For Word docs, use signed URL approach
      const url = await generateSignedUrl(resume.originalFilePath);
      if (url) {
        setSignedUrl(url);
        setShowPreview(true);
      }
    }
  };

  const sortResumes = (resumes: SavedResume[], sortBy: string): SavedResume[] => {
    const sorted = [...resumes];
    
    switch (sortBy) {
      case 'date-desc':
        return sorted.sort((a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime());
      case 'date-asc':
        return sorted.sort((a, b) => new Date(a.dateCreated).getTime() - new Date(b.dateCreated).getTime());
      case 'title-asc':
        return sorted.sort((a, b) => a.title.localeCompare(b.title));
      case 'title-desc':
        return sorted.sort((a, b) => b.title.localeCompare(a.title));
      case 'filename-asc':
        return sorted.sort((a, b) => (a.fileName || '').localeCompare(b.fileName || ''));
      case 'filename-desc':
        return sorted.sort((a, b) => (b.fileName || '').localeCompare(a.fileName || ''));
      default:
        return sorted;
    }
  };

  const sortedResumes = sortResumes(savedResumes, sortBy);
  
  // Pagination calculations
  const totalPages = Math.ceil(sortedResumes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedResumes = sortedResumes.slice(startIndex, endIndex);
  
  console.log('ResumeLibraryEnhanced Pagination Debug:', {
    totalResumes: sortedResumes.length,
    itemsPerPage,
    totalPages,
    currentPage
  });

  // Reset page when sortBy changes
  useEffect(() => {
    setCurrentPage(1);
  }, [sortBy]);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-96">
          <CardHeader>
            <CardTitle>Authentication Required</CardTitle>
            <CardDescription>Please log in to access your resume library.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading your resume library...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Custom Header matching tailored documents library page */}
      <div className="bg-gradient-to-r from-accent-light/30 to-primary-light/20 border-b border-border shadow-sm backdrop-blur-sm">
        <div className="container mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={onBack}
                className="h-9 w-9 p-0"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="space-y-1">
                <h1 className="text-2xl font-bold text-foreground">Saved/Uploaded Resumes</h1>
                <p className="text-sm text-muted-foreground">Resumes you've uploaded and saved to your library</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-primary/10 to-accent/10 rounded-full border border-primary/20 shadow-sm">
              <span className="text-xs font-medium text-muted-foreground">powered by</span>
              <span className="text-sm font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">TailorWiz</span>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto py-6 space-y-6">
        {/* Centered gradient headline */}
        <div className="text-center mb-8">
          <h2 className="text-4xl font-bold mb-4">
            <span className="bg-gradient-to-r from-primary via-accent to-primary-hover bg-clip-text text-transparent">
              Your Resume Library
            </span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Access and manage all your uploaded resumes in one centralized location. Upload, preview, and organize your professional documents.
          </p>
        </div>

        <div className="max-w-4xl mx-auto space-y-6">
          {/* Resumes List */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Saved Resumes ({savedResumes.length})</CardTitle>
                <CardDescription>
                  Uploaded resumes saved to your library
                </CardDescription>
              </div>
              <Button 
                onClick={triggerFileUpload}
                disabled={isUploading}
                className="flex items-center gap-2 shrink-0"
              >
                {isUploading ? (
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                {isUploading ? "Uploading..." : "Upload New Resume"}
              </Button>
            </CardHeader>
            <CardContent>
              {savedResumes.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No resumes found</h3>
                  <p className="text-muted-foreground">
                    Run resume analysis to start building your library.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Controls Header */}
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-4">
                      {/* Bulk Selection */}
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={selectedResumes.size === savedResumes.length && savedResumes.length > 0}
                          onCheckedChange={(checked) => handleSelectAll(checked as boolean)}
                        />
                        <span className="text-sm font-medium">
                          {selectedResumes.size === 0 
                            ? "Select All" 
                            : `${selectedResumes.size} of ${savedResumes.length} selected`
                          }
                        </span>
                      </div>
                      
                      {/* Sort Controls */}
                      <div className="flex items-center gap-2">
                        <ArrowUpDown className="w-4 h-4 text-muted-foreground" />
                        <Select value={sortBy} onValueChange={setSortBy}>
                          <SelectTrigger className="w-48">
                            <SelectValue placeholder="Sort by..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="date-desc">Date (Newest First)</SelectItem>
                            <SelectItem value="date-asc">Date (Oldest First)</SelectItem>
                            <SelectItem value="title-asc">Title (A-Z)</SelectItem>
                            <SelectItem value="title-desc">Title (Z-A)</SelectItem>
                            <SelectItem value="filename-asc">File Name (A-Z)</SelectItem>
                            <SelectItem value="filename-desc">File Name (Z-A)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    {selectedResumes.size > 0 && (
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => setShowBulkDeleteDialog(true)}
                        className="flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete Selected ({selectedResumes.size})
                      </Button>
                    )}
                  </div>

                  {paginatedResumes.map((resume) => {
                    // Determine icon and colors based on file type
                    const isPDF = resume.fileType === 'application/pdf';
                    const isWord = resume.fileType?.includes('wordprocessingml') || 
                                   resume.fileType?.includes('msword') ||
                                   resume.fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                                   resume.fileType === 'application/msword';
                    
                    const getCardBg = () => {
                      if (selectedResumes.has(resume.id)) return 'border-4 border-primary bg-gradient-to-br from-primary/10 via-primary/5 to-background';
                      if (isPDF) return 'bg-gradient-to-br from-red-50/50 via-background to-red-50/30 hover:from-red-50 hover:to-red-100/50 border-2 border-red-400/70 hover:border-red-500';
                      if (isWord) return 'bg-gradient-to-br from-blue-50/50 via-background to-blue-50/30 hover:from-blue-50 hover:to-blue-100/50 border-2 border-blue-400/70 hover:border-blue-500';
                      return 'bg-gradient-to-br from-slate-50/50 via-background to-slate-50/30 hover:from-slate-50 hover:to-slate-100/50 border-slate-200/50';
                    };
                    
                    const getIconBg = () => {
                      if (isPDF) return 'bg-gradient-to-br from-red-100 to-red-200 border-red-300 text-red-700';
                      if (isWord) return 'bg-gradient-to-br from-blue-100 to-blue-200 border-blue-300 text-blue-700';
                      return 'bg-gradient-to-br from-slate-100 to-slate-200 border-slate-300 text-slate-700';
                    };
                    
                    return (
                      <Card 
                        key={resume.id}
                        className={`cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] transform ${getCardBg()}`}
                        onClick={() => setSelectedResume(resume)}
                      >
                        <CardContent className="p-6">
                          <div className="flex items-center gap-6">
                            {/* Selection Checkbox */}
                            <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={selectedResumes.has(resume.id)}
                                onCheckedChange={(checked) => handleSelectResume(resume.id, checked as boolean)}
                              />
                            </div>
                            
                            {/* Enhanced File Icon */}
                            <div className={`w-20 h-20 rounded-xl border-2 flex items-center justify-center flex-shrink-0 relative ${getIconBg()}`}>
                              {isPDF ? (
                                <img src={pdfLogo} alt="PDF" className="w-12 h-12 object-contain" />
                              ) : isWord ? (
                                <img src={microsoftWordLogo} alt="Microsoft Word" className="w-12 h-12 object-contain" />
                              ) : (
                                <FileText className="w-10 h-10" />
                              )}
                              {selectedResumes.has(resume.id) && (
                                <div className="absolute -top-2 -right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                                  <Sparkles className="w-3 h-3 text-primary-foreground" />
                                </div>
                              )}
                            </div>

                            {/* Resume Info */}
                            <div className="flex-1 space-y-2">
                              <h3 className="font-semibold text-lg">{resume.title}</h3>
                              
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Calendar className="w-4 h-4" />
                                {formatDate(resume.dateCreated)}
                              </div>

                              <div className="flex flex-wrap gap-2">
                                {resume.jobTitle && (
                                  <Badge variant="outline" className="flex items-center gap-1 bg-green-50/50 border-green-200 text-green-700">
                                    <Target className="w-3 h-3" />
                                    {resume.jobTitle}
                                  </Badge>
                                )}
                                
                                {resume.companyName && (
                                  <Badge variant="outline" className="flex items-center gap-1 bg-purple-50/50 border-purple-200 text-purple-700">
                                    <Building className="w-3 h-3" />
                                    {resume.companyName}
                                  </Badge>
                                )}

                                {resume.fileName && (
                                  <Badge variant="secondary" className="bg-amber-50/50 border-amber-200 text-amber-700">
                                    {resume.fileName}
                                  </Badge>
                                )}
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="flex flex-col gap-2 flex-shrink-0">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedResume(resume);
                                }}
                                title="View Details"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 hover:bg-green-50 hover:text-green-600"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownload(resume);
                                }}
                                title="Download Resume"
                                disabled={!resume.originalFilePath}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 hover:bg-destructive/10 hover:text-destructive"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteResume(resume.id, resume.title);
                                }}
                                disabled={isDeleting === resume.id}
                                title="Delete Resume"
                              >
                                {isDeleting === resume.id ? (
                                  <div className="w-4 h-4 border border-current border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-sm text-muted-foreground">
                    Showing {startIndex + 1}-{Math.min(endIndex, sortedResumes.length)} of {sortedResumes.length} items
                  </div>
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious 
                          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                          className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                        />
                      </PaginationItem>
                      
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <PaginationItem key={page}>
                          <PaginationLink
                            onClick={() => setCurrentPage(page)}
                            isActive={currentPage === page}
                            className="cursor-pointer"
                          >
                            {page}
                          </PaginationLink>
                        </PaginationItem>
                      ))}
                      
                      <PaginationItem>
                        <PaginationNext 
                          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                          className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Resume Details Dialog */}
      <Dialog open={!!selectedResume} onOpenChange={() => setSelectedResume(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {selectedResume?.title}
            </DialogTitle>
            <DialogDescription>
              Full resume content
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {selectedResume && (
              <>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <label className="font-medium text-muted-foreground">Created</label>
                    <p>{formatDate(selectedResume.dateCreated)}</p>
                  </div>
                  
                  {selectedResume.fileName && (
                    <div>
                      <label className="font-medium text-muted-foreground">Original File</label>
                      <p>{selectedResume.fileName}</p>
                    </div>
                  )}
                  
                  {selectedResume.jobTitle && (
                    <div>
                      <label className="font-medium text-muted-foreground">Target Position</label>
                      <p>{selectedResume.jobTitle}</p>
                    </div>
                  )}
                  
                  {selectedResume.companyName && (
                    <div>
                      <label className="font-medium text-muted-foreground">Target Company</label>
                      <p>{selectedResume.companyName}</p>
                    </div>
                  )}
                </div>

                <Separator />

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="font-medium">Resume Actions</label>
                    <div className="flex gap-2">
                      {(selectedResume.originalFileUrl || selectedResume.wordDocumentUrl) && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handlePreviewClick(selectedResume)}
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Preview Document
                        </Button>
                      )}
                      {selectedResume.content && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            const blob = new Blob([selectedResume.content], { type: 'text/plain' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `${selectedResume.title}.txt`;
                            a.click();
                            URL.revokeObjectURL(url);
                          }}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Export Text
                        </Button>
                      )}
                    </div>
                  </div>
                  {selectedResume.content && (
                    <div className="bg-muted p-4 rounded-lg max-h-[400px] overflow-y-auto whitespace-pre-wrap text-sm">
                      {selectedResume.content}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Single Delete Confirmation Dialog */}
      <AlertDialog open={!!showDeleteDialog} onOpenChange={() => setShowDeleteDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Resume</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{showDeleteDialog?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteResume}
              disabled={!!isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Selected Resumes</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedResumes.size} resume{selectedResumes.size !== 1 ? 's' : ''}? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBulkDelete}
              disabled={isBulkDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isBulkDeleting ? "Deleting..." : "Delete Resumes"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Resume Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={(open) => {
        setShowPreview(open);
        if (!open && signedUrl) {
          // Clean up blob URLs to prevent memory leaks
          if (signedUrl.startsWith('blob:')) {
            URL.revokeObjectURL(signedUrl);
          }
          setSignedUrl(null);
        }
      }}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Preview: {selectedResume?.title}
            </DialogTitle>
            <DialogDescription>
              Document preview
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden">
            {selectedResume && signedUrl && (
              <>
                {/* PDF Preview */}
                {selectedResume.fileType === 'application/pdf' && (
                  <div className="h-[70vh]">
                    <div className="mb-2 text-sm text-muted-foreground">
                      Loading PDF: {selectedResume.title}
                    </div>
                    <PDFViewer 
                      fileUrl={signedUrl}
                      fileName={selectedResume.fileName || selectedResume.title}
                      className="w-full h-full"
                    />
                  </div>
                )}
                
                {/* Word Document Preview */}
                {(selectedResume.fileType?.includes('wordprocessingml') || 
                  selectedResume.fileType?.includes('msword') ||
                  selectedResume.fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                  selectedResume.fileType === 'application/msword') && (
                  <div className="h-[70vh]">
                    <WordOnlineViewer 
                      documentUrl={signedUrl}
                      fileName={selectedResume.fileName || selectedResume.title}
                      className="w-full h-full"
                      height="100%"
                    />
                  </div>
                )}
                
                {/* Debug info for file type */}
                <div className="mt-4 p-3 bg-muted rounded text-xs">
                  <p><strong>File Type:</strong> {selectedResume.fileType}</p>
                  <p><strong>URL Type:</strong> {signedUrl.startsWith('blob:') ? 'Blob URL (PDF)' : 'Signed URL (Word)'}</p>
                  <p><strong>URL Preview:</strong> {signedUrl.substring(0, 100)}...</p>
                  <p><strong>Original Path:</strong> {selectedResume.originalFilePath}</p>
                </div>
              </>
            )}
            
            {selectedResume && !signedUrl && (
              <div className="flex items-center justify-center h-[400px] text-muted-foreground">
                <div className="text-center">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No document preview available</p>
                  <p className="text-sm">Unable to access file or file path missing</p>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}